/**
 * Bridge protocol — shared between the iframe-side client and the host-side
 * router. Messages flow over postMessage. The host is authoritative.
 */

import type {DocumentShareSnapshot} from '../entities/document-share';
import type {ImageSnapshot} from '../entities/image';

/**
 * Discriminated union of every entity snapshot the runtime knows how to
 * carry. New entity types extend this union as they ship — adding one is
 * an explicit edit, which is the right behavior: the runtime should know
 * about every entity it might receive so user TSX can narrow the type via
 * the `type` discriminator.
 *
 * The shape mirrors the GraphQL `CanvasEntityInterface` plus per-type
 * fields, so the host adapter can map straight through from the GraphQL
 * response without reshaping.
 */
export type EntitySnapshot = DocumentShareSnapshot | ImageSnapshot;

export type CanvasFontStyle = 'SANS_SERIF' | 'SERIF';
export type CanvasCornerStyle = 'ROUNDED' | 'SQUARE';

export interface CanvasBrand {
  primary?: string | null;
  accent?: string | null;
  fontStyle?: CanvasFontStyle | null;
  cornerStyle?: CanvasCornerStyle | null;
  companyName?: string | null;
}

/**
 * Reserved public id for the org's brand-logo image entity. Injected by the
 * host as a synthetic entity (like the `img:compliance:*` ids) when the
 * brand profile has a logo; `<BrandMark />` renders it and renders nothing
 * when it's absent. Real entities are `ent_`-prefixed, so this can never
 * collide.
 */
export const BRAND_LOGO_ENTITY_ID = 'img:brand:logo';

/**
 * Compliance footer config consumed by a runtime-owned React subtree. Not
 * exposed to user TSX through any imported hook, so LLM-emitted code can't
 * read or forge these values — DOM-level observation/removal of the
 * rendered footer is still possible since both subtrees share the iframe
 * document.
 */
export interface ComplianceConfig {
  disclosure?: string | null;
  companyName?: string | null;
  companyImageId?: string | null;
  dealerEnabled?: boolean | null;
  dealerName?: string | null;
  dealerImageId?: string | null;
}

export interface AdvisorConfig {
  name?: string | null;
  email?: string | null;
  avatarImageId?: string | null;
}

export interface BootstrapPayload {
  source: string;
  /**
   * `source` with `data-cn-src` annotations spliced in by the host (see
   * `annotate/`). When present the runtime compiles this instead, but still
   * reports `source` as what rendered so render acks hash the original.
   */
  annotatedSource?: string;
  entities: EntitySnapshot[];
  brand?: CanvasBrand | null;
  compliance?: ComplianceConfig | null;
  advisor?: AdvisorConfig | null;
}

/**
 * How a host element's text children appear in the TSX that produced it.
 * `literal` is static JSX text, `expr` is one or more `{...}` containers,
 * `mixed` combines text, expressions, and/or inline child elements.
 */
export type SourceTextKind = 'literal' | 'expr' | 'mixed' | 'elements';

export interface SourceElementMeta {
  /** `line:column` of the JSX opening element, 1-based like an editor. */
  src: string;
  tag: string;
  kind: SourceTextKind;
  /** Raw source of each `{...}` text child, e.g. `text`, `item.title`. */
  exprs: string[];
  /** Whether every text child is something a source edit can target. */
  editable: boolean;
  /** Callee of the closest enclosing `.map(...)`, e.g. `testCards.map`. */
  loop: string | null;
  /** Name of the enclosing component or helper function, when named. */
  component: string | null;
  /** Raw JSX between the opening and closing tags, trimmed. */
  childrenSource: string;
}

/** What the runtime reports for one committed inline edit. */
export interface TextEditReport {
  /** Stable per-render identity: `${src}#${instanceIndex}`. */
  key: string;
  src: string;
  tag: string;
  className: string | null;
  /** Index of this element among all rendered elements sharing `src`. */
  instanceIndex: number;
  instanceCount: number;
  /** Rendered text of every sibling instance, when the element repeats. */
  siblingTexts: string[] | null;
  /** Annotated ancestors, outermost first: `tag.class@line:col`. */
  path: string[];
  before: string;
  after: string;
  /** True when before/after carry minimal inline HTML rather than text. */
  isHtml: boolean;
}

/** A report joined with the host's annotation table entry for its element. */
export interface TextEdit extends TextEditReport {
  meta: SourceElementMeta | null;
}

export type IframeMessage =
  | {kind: 'bootstrap.request'}
  | {kind: 'textedit.change'; edit: TextEditReport}
  | {kind: 'textedit.commit.result'; requestId: string}
  | {
      kind: 'entity.dispatch';
      id: string;
      action: string;
      payload: Record<string, unknown>;
      requestId: string;
    }
  | {kind: 'runtime.error'; message: string; stack?: string}
  | {kind: 'runtime.rendered'; occurredAt: string; source: string}
  | {kind: 'runtime.size'; height: number}
  | {kind: 'snapshot.result'; requestId: string; html: string; width: number}
  | {kind: 'print.request'}
  | {kind: 'link.open'; href: string}
  | {kind: 'drag.over'}
  | {kind: 'drag.end'}
  | {kind: 'drag.drop'; files: File[]};

export type HostMessage =
  | {kind: 'bootstrap'; payload: BootstrapPayload}
  | {kind: 'source.update'; source: string; annotatedSource?: string}
  | {kind: 'brand.update'; brand: CanvasBrand | null}
  | {kind: 'textedit.mode'; enabled: boolean}
  | {kind: 'textedit.commit'; requestId: string}
  | {kind: 'textedit.reset'}
  | {kind: 'snapshot.request'; requestId: string}
  | {
      kind: 'entity.dispatch.result';
      requestId: string;
      ok: true;
      data?: unknown;
    }
  | {
      kind: 'entity.dispatch.result';
      requestId: string;
      ok: false;
      error: string;
    };

/**
 * Observation event emitted by `CanvasBridgeRouter.observe`. `in` is a
 * message arriving from the iframe; `out` is one leaving the host. Used by
 * dev/debug surfaces to render a live bridge log.
 */
export interface BridgeObservation {
  direction: 'in' | 'out';
  message: IframeMessage | HostMessage;
  occurredAt: string;
}

export interface BridgeEnvelope<T> {
  protocol: 'canvas-runtime';
  message: T;
}

export function envelope<T>(message: T): BridgeEnvelope<T> {
  return {protocol: 'canvas-runtime', message};
}

export function isEnvelope(
  value: unknown,
): value is BridgeEnvelope<{kind: string}> {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const candidate = value as {protocol?: unknown; message?: unknown};
  if (candidate.protocol !== 'canvas-runtime') {
    return false;
  }
  if (!candidate.message || typeof candidate.message !== 'object') {
    return false;
  }
  return typeof (candidate.message as {kind?: unknown}).kind === 'string';
}

/**
 * The iframe runs untrusted page code that can post anything to the parent,
 * so a text-edit report is checked field by field before the host acts on it.
 */
export function isTextEditReport(value: unknown): value is TextEditReport {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const r = value as Record<string, unknown>;
  const isStringArrayOrNull = (v: unknown): boolean =>
    v === null || (Array.isArray(v) && v.every((s) => typeof s === 'string'));
  return (
    typeof r.key === 'string' &&
    typeof r.src === 'string' &&
    typeof r.tag === 'string' &&
    (r.className === null || typeof r.className === 'string') &&
    typeof r.instanceIndex === 'number' &&
    typeof r.instanceCount === 'number' &&
    isStringArrayOrNull(r.siblingTexts) &&
    Array.isArray(r.path) &&
    r.path.every((p) => typeof p === 'string') &&
    typeof r.before === 'string' &&
    typeof r.after === 'string' &&
    typeof r.isHtml === 'boolean'
  );
}
